import Link from 'next/link'
import { ReactElement, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import validator from 'validator'

import AuthNotification from '@/components/AuthNotification'
import { ButtonWithLoading, ContinueWith, SocialButtonGroup } from '@/components/buttons'
import { Checkbox, PhoneNumberInput, TextInput } from '@/components/FormElement'
import AuthLayout from '@/components/Layouts/AuthLayout'
import { useAuth } from '@/hooks/Auth'
import { useLanguage, useReferralCtx } from '@/hooks/Contexts'
import { useGTM } from '@/hooks/GoogleTagManager'
import { API_URL } from '@/utils/Consts'
import { safeSessionStorage } from '@/utils/SafeStorage'

function Register(): ReactElement {
  const { auth: authStr, errors: errStr } = useLanguage()
  const refCtx = useReferralCtx()

  const pathBeforeAuth = useRef('/user/dashboard')

  useEffect(() => {
    pathBeforeAuth.current = safeSessionStorage.getItem('pathBeforeAuth') ?? '/user/dashboard'
    safeSessionStorage.clear()
  }, [])

  const {
    doAuth,
    isLoading,
    errors: authError,
    getSocialLoginLink,
  } = useAuth({
    middleware: 'guest',
    redirectIfAuthenticated: pathBeforeAuth.current,
  })

  const form = useForm({
    mode: 'onBlur',
    defaultValues: { email: '', password: '', phone_num: '', date_of_birth: '', agree_terms: false },
  })
  const {
    register,
    formState: { errors },
    setValue,
    handleSubmit,
  } = form

  const { logEventGTM } = useGTM()

  // 校验 date_of_birth：格式必须为 DD/MM/YYYY，且年龄需满 18 周岁
  const validateDob = (value: string) => {
    const matched = value?.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
    if (!matched) return errStr.dob_invalid_format
    const day = Number(matched[1])
    const month = Number(matched[2])
    const year = Number(matched[3])
    const dob = new Date(year, month - 1, day)
    if (dob.getFullYear() !== year || dob.getMonth() !== month - 1 || dob.getDate() !== day) return errStr.dob_invalid_format
    const today = new Date()
    let age = today.getFullYear() - year
    const beforeBirthday = today.getMonth() < month - 1 || (today.getMonth() === month - 1 && today.getDate() < day)
    if (beforeBirthday) age -= 1
    return age >= 18 || errStr.must_be_18
  }

  const submit = data => {
    const params = ['email', 'password', 'phone_num', 'date_of_birth'].reduce(
      (obj, key) => {
        obj[key] = data[key]
        return obj
      },
      refCtx?.rc ? { rc: refCtx.rc } : {}
    )

    doAuth(API_URL.register, params, () => {
      logEventGTM({
        dataLayer: {
          event: 'registration_success',
          event_category: 'register',
          event_action: 'action_complete',
          event_label: 'Registration Third Step',
        },
      })
    })
  }

  return (
    <AuthLayout type={1}>
      {authError && <AuthNotification message={authError} className='mt-6' />}
      <form onSubmit={handleSubmit(submit)} className='mt-6 w-full lg:w-96'>
        <TextInput
          label={authStr.email}
          input={{
            'type': 'email',
            'inputMode': 'email',
            'autoComplete': 'email',
            'autoCapitalize': 'none',
            'autoCorrect': 'off',
            'spellCheck': false,
            'placeholder': authStr.email_placeholder,
            'disabled': isLoading,
            ...register('email', {
              // Trim value read from the input: iOS keyboards / Facebook in-app browser autofill
              // often append whitespace, which makes validator.isEmail() reject a valid address.
              setValueAs: value => (typeof value === 'string' ? value.trim() : value),
              required: authStr.email + errStr.required,
              validate: value => {
                if (value) return validator.isEmail(value) || errStr.email_invalid
              },
            }),
            // @ts-ignore
            'data-testid': 'inp-register-email',
          }}
          required={true}
          showValidated={!!errors?.email}
          valid={!errors?.email}
          tipMessage={errors?.email?.message?.toString() ?? null}
        />

        <TextInput
          outerCls='mt-6'
          label={authStr.password}
          input={{
            'type': 'password',
            'autoComplete': 'new-password',
            'placeholder': authStr.password_placeholder,
            'disabled': isLoading,
            ...register('password', {
              required: authStr.new_password + errStr.required,
              minLength: { value: 8, message: errStr.password_too_short },
              validate: {
                notBlank: value => (value?.trim().length ?? 0) >= 8 || errStr.password_too_short,
                // notWeak: 旧规则保留，新增 notBlank 仅拦截 trim 后不足 8 字符的密码
                notWeak: value => /(^1+$)/g.test(value) !== true || errStr.password_too_weak[0],
              },
            }),
            // @ts-ignore
            'data-testid': 'inp-register-pwd',
          }}
          required={true}
          showValidated={!!errors?.password}
          valid={!errors?.password}
          tipMessage={errors?.password?.message?.toString() ?? null}
          visibility
        />

        <PhoneNumberInput
          isAddress={false}
          form={form}
          extraCls='mt-6'
          // @ts-ignore
          numberInputProps={{ 'data-testid': 'inp-register-phone' }}
          checkIconCls='left-[calc(100vw-50px)] sm:left-[calc(100vw-66px)] md:left-[calc(100vw-82px)] lg:left-[334px]'
        />
        <p className='mt-2 text-xs text-neutral-600'>Please enter carefully as we contact all our winners by phone.</p>

        <TextInput
          outerCls='mt-6'
          label={authStr.date_of_birth}
          input={{
            'type': 'text',
            'inputMode': 'numeric',
            'autoComplete': 'bday',
            'maxLength': 10,
            'placeholder': authStr.date_of_birth_placeholder,
            'disabled': isLoading,
            ...register('date_of_birth', { required: authStr.date_of_birth + errStr.required, validate: validateDob }),
            // 只保留数字并按 DD/MM/YYYY 自动插入分隔符；用 setValue 写回（非受控输入直接改 e.target.value 不可靠）
            'onChange': e => {
              const digits = e.target.value.replace(/\D/g, '').slice(0, 8)
              const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean)
              setValue('date_of_birth', parts.join('/'), { shouldValidate: !!errors?.date_of_birth })
            },
            // @ts-ignore
            'data-testid': 'inp-register-dob',
          }}
          required={true}
          showValidated={!!errors?.date_of_birth}
          valid={!errors?.date_of_birth}
          tipMessage={errors?.date_of_birth?.message?.toString() ?? null}
        />

        <Checkbox
          extraCls='mt-6 items-start text-sm'
          innerCls='!top-1 !translate-y-0'
          input={{
            ...register('agree_terms', { required: errStr.must_agree_terms }),
            // @ts-ignore
            'data-testid': 'ckb-register-agree',
          }}
          text={
            <span className='ml-2 text-neutral-600'>
              {authStr.confirm_age_terms}
              <Link href='/terms-conditions'>
                <span className='cursor-pointer text-cyan-v2 hover:text-amber-v1'>{authStr.terms_conditions_link}</span>
              </Link>
              .
            </span>
          }
        />
        {errors?.agree_terms && <div className='mt-1 text-xs text-error'>{errors.agree_terms.message?.toString()}</div>}

        <ButtonWithLoading
          type='submit'
          title={authStr.register_now}
          isLoading={isLoading}
          className='btn-rd-amber btn-hover-amber btn-disabled-grey mx-auto mt-6 h-10 w-full max-w-sm md:h-8'
          data-testid='btn-register-confirm'
        />

        <ContinueWith className='mx-auto mt-6 flex max-w-sm flex-row items-center' />
        <SocialButtonGroup socialLogin={getSocialLoginLink} className='mx-auto mt-6 max-w-sm' />
      </form>
    </AuthLayout>
  )
}

export default Register
